/**
 * EnumDefinitions.jsx
 * Allows admins to define meanings for enum/status columns (shared across all admins)
 * Example: status=1 means "Pending", payment_type='CC' means "Credit Card"
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Hash, Check, X, Sparkles, ChevronRight, Search, 
  Database, Table2, Columns, Save, RefreshCw, AlertCircle,
  CheckCircle2, Edit2, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  discoverEnumColumns, getEnumDefinitions, saveEnumDefinition,
  deleteEnumDefinition, getEnumValuesFromDB
} from '../../services/api';

const EnumDefinitions = ({ onComplete }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [state, setState] = useState({
    loading: true,
    enumColumns: [],
    definitions: {},
    currentColumn: null,
    editingDefinitions: {},
    notes: '',
    aiSuggesting: false,
    searchQuery: '',
    showDefined: false
  });

  const loadData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const [columnsRes, defsRes] = await Promise.all([
        discoverEnumColumns(),
        getEnumDefinitions()
      ]);

      // Create a lookup for existing definitions
      const defsLookup = {};
      for (const def of defsRes.definitions || []) {
        const key = `${def.server}|${def.database}|${def.table}|${def.column}`;
        defsLookup[key] = def;
      }

      setState(prev => ({
        ...prev,
        loading: false,
        enumColumns: columnsRes.enumColumns || [],
        definitions: defsLookup
      }));

      // Calculate progress
      const totalDefined = Object.keys(defsLookup).length;
      const totalColumns = columnsRes.enumColumns?.length || 1;
      const progress = Math.min(100, Math.round((totalDefined / totalColumns) * 100));
      onCompleteRef.current(progress);

    } catch (error) {
      console.error('Failed to load enum columns:', error);
      toast.error('Failed to load enum columns');
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectColumn = (column) => {
    const key = `${column.server}|${column.database}|${column.table}|${column.column}`;
    const existingDef = state.definitions[key];

    setState(prev => ({
      ...prev,
      currentColumn: column,
      editingDefinitions: existingDef?.definitions || {},
      notes: existingDef?.notes || ''
    }));
  };

  const handleAISuggest = async () => {
    if (!state.currentColumn) return;

    setState(prev => ({ ...prev, aiSuggesting: true }));
    try {
      // Query actual DISTINCT values from MySQL, then AI suggests meanings
      const result = await getEnumValuesFromDB(
        state.currentColumn.server,
        state.currentColumn.database,
        state.currentColumn.table,
        state.currentColumn.column
      );

      if (result.success) {
        // Show actual values found
        const valuesFound = result.values?.length || 0;
        const dateFiltered = result.dateFiltered;
        
        if (result.suggestions && Object.keys(result.suggestions).length > 0) {
          setState(prev => ({
            ...prev,
            editingDefinitions: { ...prev.editingDefinitions, ...result.suggestions },
            aiSuggesting: false,
            discoveredValues: result.values
          }));
          
          const filterNote = dateFiltered ? ' (filtered to recent data due to large table)' : '';
          toast.success(`Found ${valuesFound} values${filterNote}. AI suggestions added - please review!`);
        } else if (result.values?.length > 0) {
          // Values found but no AI suggestions - add values as empty definitions
          const emptyDefs = {};
          result.values.forEach(v => { emptyDefs[v] = ''; });
          setState(prev => ({
            ...prev,
            editingDefinitions: { ...prev.editingDefinitions, ...emptyDefs },
            aiSuggesting: false,
            discoveredValues: result.values
          }));
          toast.info(`Found ${valuesFound} values. Please define their meanings.`);
        } else {
          toast.warning('No values found in this column');
          setState(prev => ({ ...prev, aiSuggesting: false }));
        }
      } else {
        // Show more specific error message for MySQL connection issues
        const errorMsg = result.error || 'Failed to query values';
        if (errorMsg.includes("Can't connect to MySQL") || errorMsg.includes("2003")) {
          toast.error('Cannot connect to MySQL server. Please check your AWS Security Group allows connections from this server.', { duration: 8000 });
        } else {
          toast.error(errorMsg, { duration: 5000 });
        }
        setState(prev => ({ ...prev, aiSuggesting: false }));
      }
    } catch (error) {
      console.error('AI suggestion failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      if (errorMsg.includes("Can't connect to MySQL") || errorMsg.includes("2003")) {
        toast.error('Cannot connect to MySQL server. Please verify your database is accessible and Security Groups allow traffic.', { duration: 8000 });
      } else {
        toast.error(`Failed to get values: ${errorMsg}`);
      }
      setState(prev => ({ ...prev, aiSuggesting: false }));
    }
  };

  const addDefinitionRow = () => {
    setState(prev => ({
      ...prev,
      editingDefinitions: { ...prev.editingDefinitions, '': '' }
    }));
  };

  const updateDefinition = (oldKey, newKey, newValue) => {
    setState(prev => {
      const updated = { ...prev.editingDefinitions };
      if (oldKey !== newKey) {
        delete updated[oldKey];
      }
      updated[newKey] = newValue;
      return { ...prev, editingDefinitions: updated };
    });
  };

  const removeDefinition = (key) => {
    setState(prev => {
      const updated = { ...prev.editingDefinitions };
      delete updated[key];
      return { ...prev, editingDefinitions: updated };
    });
  };

  const handleSave = async () => {
    if (!state.currentColumn) return;

    // Filter out empty keys
    const cleanDefinitions = {};
    for (const [k, v] of Object.entries(state.editingDefinitions)) {
      if (k.trim()) {
        cleanDefinitions[k.trim()] = v.trim();
      }
    }

    if (Object.keys(cleanDefinitions).length === 0) {
      toast.error('Please add at least one value definition');
      return;
    }

    try {
      await saveEnumDefinition({
        server: state.currentColumn.server,
        database: state.currentColumn.database,
        table: state.currentColumn.table,
        column: state.currentColumn.column,
        definitions: cleanDefinitions,
        notes: state.notes
      });

      // Update local state
      const key = `${state.currentColumn.server}|${state.currentColumn.database}|${state.currentColumn.table}|${state.currentColumn.column}`;
      setState(prev => ({
        ...prev,
        definitions: {
          ...prev.definitions,
          [key]: {
            ...state.currentColumn,
            definitions: cleanDefinitions,
            notes: state.notes
          }
        },
        enumColumns: prev.enumColumns.map(c => 
          c.server === state.currentColumn.server && 
          c.database === state.currentColumn.database && 
          c.table === state.currentColumn.table && 
          c.column === state.currentColumn.column
            ? { ...c, hasDefined: true }
            : c
        ),
        currentColumn: null,
        editingDefinitions: {},
        notes: ''
      }));

      toast.success(`Saved! All admins will see these definitions for ${state.currentColumn.column}`);

      // Update progress
      const totalDefined = Object.keys(state.definitions).length + 1;
      const totalColumns = state.enumColumns.length || 1;
      onCompleteRef.current(Math.min(100, Math.round((totalDefined / totalColumns) * 100)));

    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save definitions');
    }
  };

  const handleDelete = async (column) => {
    if (!window.confirm(`Delete definitions for ${column.table}.${column.column}?`)) return;

    try {
      await deleteEnumDefinition(column.server, column.database, column.table, column.column);

      const key = `${column.server}|${column.database}|${column.table}|${column.column}`;
      setState(prev => {
        const newDefs = { ...prev.definitions };
        delete newDefs[key];
        return {
          ...prev,
          definitions: newDefs,
          enumColumns: prev.enumColumns.map(c =>
            c.server === column.server &&
            c.database === column.database &&
            c.table === column.table &&
            c.column === column.column
              ? { ...c, hasDefined: false }
              : c
          )
        };
      });

      toast.success('Definition deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filteredColumns = state.enumColumns.filter(col => {
    const matchesSearch = !state.searchQuery || 
      col.column.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      col.table.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      col.database.toLowerCase().includes(state.searchQuery.toLowerCase());
    
    // If searching, show ALL matching columns regardless of defined status
    // If not searching, filter by the tab selection
    if (state.searchQuery && state.searchQuery.length > 0) {
      return matchesSearch;  // Show all matching columns when searching
    }
    
    const matchesFilter = state.showDefined ? col.hasDefined : !col.hasDefined;
    return matchesSearch && matchesFilter;
  });

  const definedCount = state.enumColumns.filter(c => c.hasDefined).length;
  const pendingCount = state.enumColumns.length - definedCount;

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-500 mr-2" />
        <span className="text-gray-500">Discovering enum columns...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Enum & Status Definitions</h3>
          <p className="text-sm text-gray-500">
            Define what values like status=1 or type='CC' mean. Shared across all admins.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm">
            <span className="text-green-600 font-medium">{definedCount}</span>
            <span className="text-gray-400"> defined / </span>
            <span className="text-amber-600 font-medium">{pendingCount}</span>
            <span className="text-gray-400"> pending</span>
          </div>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <Progress value={(definedCount / Math.max(1, state.enumColumns.length)) * 100} className="h-2" />
        <div className="text-xs text-gray-400 text-right">
          {Math.round((definedCount / Math.max(1, state.enumColumns.length)) * 100)}% complete
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Column List */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Enum Columns</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant={!state.showDefined ? "default" : "outline"}
                  size="sm"
                  onClick={() => setState(prev => ({ ...prev, showDefined: false }))}
                  className={!state.showDefined ? "" : "hover:bg-amber-50 hover:border-amber-300 cursor-pointer"}
                >
                  Pending ({pendingCount})
                </Button>
                <Button
                  variant={state.showDefined ? "default" : "outline"}
                  size="sm"
                  onClick={() => setState(prev => ({ ...prev, showDefined: true }))}
                  className={state.showDefined ? "" : "hover:bg-green-50 hover:border-green-300 cursor-pointer"}
                >
                  Defined ({definedCount})
                </Button>
              </div>
            </div>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search columns..."
                value={state.searchQuery}
                onChange={(e) => setState(prev => ({ ...prev, searchQuery: e.target.value }))}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto">
            {filteredColumns.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                {state.showDefined ? 'No defined columns yet' : 'All columns have been defined!'}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredColumns.map((col, idx) => {
                  const key = `${col.server}|${col.database}|${col.table}|${col.column}`;
                  const isSelected = state.currentColumn && 
                    state.currentColumn.server === col.server &&
                    state.currentColumn.database === col.database &&
                    state.currentColumn.table === col.table &&
                    state.currentColumn.column === col.column;

                  return (
                    <div
                      key={key}
                      onClick={() => selectColumn(col)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-purple-400 bg-purple-50' 
                          : col.hasDefined 
                            ? 'border-green-200 bg-green-50 hover:border-green-300'
                            : 'border-gray-200 hover:border-purple-200 hover:bg-purple-50/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Hash className={`w-4 h-4 ${col.hasDefined ? 'text-green-500' : 'text-amber-500'}`} />
                          <span className="font-medium text-gray-800">{col.column}</span>
                          <Badge variant="outline" className="text-xs">
                            {col.columnType?.split('(')[0]}
                          </Badge>
                        </div>
                        {col.hasDefined ? (
                          <div className="flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleDelete(col); }}
                              className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <Database className="w-3 h-3" />
                        <span>{col.database}</span>
                        <Table2 className="w-3 h-3 ml-2" />
                        <span>{col.table}</span>
                        <span className="text-gray-400">({col.rowCount?.toLocaleString()} rows)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Definition Editor */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {state.currentColumn ? (
                <span>Define: <span className="text-purple-600">{state.currentColumn.column}</span></span>
              ) : (
                'Select a column to define'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!state.currentColumn ? (
              <div className="text-center py-12 text-gray-400">
                <Columns className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a column from the list to define its values</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Column info */}
                <div className="p-3 bg-gray-50 rounded-lg text-sm">
                  <div className="text-gray-600">
                    <span className="font-medium">{state.currentColumn.server}</span>
                    <span className="text-gray-400"> → </span>
                    <span className="font-medium">{state.currentColumn.database}</span>
                    <span className="text-gray-400"> → </span>
                    <span className="font-medium">{state.currentColumn.table}</span>
                    <span className="text-gray-400"> → </span>
                    <span className="font-medium text-purple-600">{state.currentColumn.column}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Type: {state.currentColumn.columnType} | Rows: {state.currentColumn.rowCount?.toLocaleString()}
                  </div>
                </div>

                {/* AI Suggest button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAISuggest}
                  disabled={state.aiSuggesting}
                  className="w-full"
                >
                  {state.aiSuggesting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      AI is thinking...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      AI Suggest Meanings
                    </>
                  )}
                </Button>

                {/* Definitions editor */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">Value Definitions</label>
                    <Button variant="ghost" size="sm" onClick={addDefinitionRow}>
                      + Add Value
                    </Button>
                  </div>
                  
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {Object.entries(state.editingDefinitions).map(([key, value], idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input
                          placeholder="Value (e.g., 1)"
                          value={key}
                          onChange={(e) => updateDefinition(key, e.target.value, value)}
                          className="w-24 font-mono"
                        />
                        <span className="text-gray-400">=</span>
                        <Input
                          placeholder="Meaning (e.g., Pending)"
                          value={value}
                          onChange={(e) => updateDefinition(key, key, e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDefinition(key)}
                          className="text-red-400 hover:text-red-600 h-8 w-8 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {Object.keys(state.editingDefinitions).length === 0 && (
                    <div className="text-center py-4 text-gray-400 text-sm border border-dashed rounded-lg">
                      Click "AI Suggest" or "+ Add Value" to start defining
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium text-gray-700">Additional Notes (optional)</label>
                  <Input
                    placeholder="e.g., Status changes automatically after payment confirmation"
                    value={state.notes}
                    onChange={(e) => setState(prev => ({ ...prev, notes: e.target.value }))}
                    className="mt-1"
                  />
                </div>

                {/* Save button */}
                <div className="flex gap-2">
                  <Button onClick={handleSave} className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    Save Definitions
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setState(prev => ({ 
                      ...prev, 
                      currentColumn: null, 
                      editingDefinitions: {}, 
                      notes: '' 
                    }))}
                  >
                    Cancel
                  </Button>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  Definitions are shared across all admin users
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EnumDefinitions;
